import React, { useState, useEffect } from 'react';
import { itemService, uploadService, mediaUrl } from '../services/api';

interface Item {
  id: number;
  name: string;
  description?: string;
  price?: number;
  isBought: boolean;
  purchaseLink?: string;
  photo?: { url: string };
  category?: { id: number; name: string };
}

interface Category {
  id: number;
  name: string;
}

interface ItemFormProps {
  item?: Item | null;
  categories: Category[];
  onSave: () => void;
  onCancel: () => void;
}

export const ItemForm: React.FC<ItemFormProps> = ({ item, categories, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    isBought: false,
    purchaseLink: '',
    category: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [existingPhoto, setExistingPhoto] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name,
        description: item.description || '',
        price: item.price?.toString() || '',
        isBought: item.isBought,
        purchaseLink: item.purchaseLink || '',
        category: item.category?.id.toString() || '',
      });
      setExistingPhoto(item.photo?.url);
    }
  }, [item]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData((prev) => ({
      ...prev,
      [name]: val,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let photoUrl: string | undefined;
      if (photoFile) {
        const uploaded = await uploadService.upload(photoFile);
        photoUrl = uploaded.url;
      }

      const submitData = {
        name: formData.name,
        description: formData.description || undefined,
        price: formData.price ? parseFloat(formData.price) : undefined,
        isBought: formData.isBought,
        purchaseLink: formData.purchaseLink || undefined,
        category: formData.category ? parseInt(formData.category) : undefined,
        // Only sent when a new file was uploaded; the backend preserves the
        // existing photo when this is omitted.
        photoUrl,
      };

      if (item) {
        await itemService.update(item.id, submitData);
      } else {
        await itemService.create(submitData);
      }

      onSave();
    } catch (err) {
      setError('Failed to save item');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800">
        {item ? 'Edit Item' : 'Add New Item'}
      </h2>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Item Name *
          </label>
          <input
            id="name"
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            placeholder="e.g., Wireless Headphones"
          />
        </div>

        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
            Category
          </label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="">Select a category</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={4}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          placeholder="Describe this item..."
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
            Price
          </label>
          <input
            id="price"
            type="number"
            name="price"
            value={formData.price}
            onChange={handleChange}
            step="0.01"
            min="0"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            placeholder="0.00"
          />
        </div>

        <div>
          <label htmlFor="purchaseLink" className="block text-sm font-medium text-gray-700 mb-2">
            Purchase Link
          </label>
          <input
            id="purchaseLink"
            type="url"
            name="purchaseLink"
            value={formData.purchaseLink}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            placeholder="https://..."
          />
        </div>
      </div>

      <div>
        <label htmlFor="photo" className="block text-sm font-medium text-gray-700 mb-2">
          Photo
        </label>
        {(photoFile || existingPhoto) && (
          <img
            src={photoFile ? URL.createObjectURL(photoFile) : mediaUrl(existingPhoto)}
            alt="Preview"
            className="w-32 h-32 object-contain rounded-lg border border-gray-200 mb-3 bg-gray-50"
          />
        )}
        <input
          id="photo"
          type="file"
          name="photo"
          accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
          onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
          className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100 cursor-pointer"
        />
        <p className="mt-1 text-xs text-gray-500">JPEG, PNG, WebP, GIF or AVIF, up to 5 MB.</p>
      </div>

      <div className="flex items-center">
        <input
          id="isBought"
          type="checkbox"
          name="isBought"
          checked={formData.isBought}
          onChange={handleChange}
          className="w-4 h-4 text-blue-500 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
        />
        <label htmlFor="isBought" className="ml-3 text-sm font-medium text-gray-700 cursor-pointer">
          Mark as bought
        </label>
      </div>

      <div className="flex gap-4 pt-4">
        <button
          type="submit"
          disabled={loading}
          className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white font-semibold py-2 px-4 rounded-lg transition"
        >
          {loading ? 'Saving...' : item ? 'Update Item' : 'Create Item'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-lg transition"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};
